import React, {useCallback, useMemo} from 'react';


import {RequiredMark} from 'src/components/required-mark';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {WidthItem} from 'src/components/width-item';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {useBusinessLines} from 'src/hooks/dictionaries/useBusinessLines';
import {
    MAP_BUSINESS_LINE_FIELD_NAMES,
    mapAccountDataToSettings,
    mapAccountSettingsToData,
} from 'src/modules/create-account/utils/mapAccountSettingsToData';
import {
    EntitlementPresetsCombobox,
} from 'src/modules/profile/account/tabs/settings/sections/entitlement-settings/entitlement-presets-combobox';

import {CheckBox} from '@pb/kit-component-checkbox';
import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import FormSection from '@pb/kit-component-form-section';
import {Group} from '@pb/kit-component-group';
import {Info} from '@pb/kit-component-info';
import Label from '@pb/kit-component-label';

import {AccountPreset} from '../entitlement-settings/useAccountPreset';

import {AccountSettingsSkeleton} from './account-settings-skeleton';
import {ACCREDITATION_STATUS_DICTIONARY} from './constants';

import type {AccessRight} from 'src/hooks/access-rights/constants';
import type {AccountSettingsData} from 'src/modules/create-account/typedef';
import type {AccountSettings} from 'src/openapi/admin/models/AccountSettings';
import type {AccreditationStatus} from 'src/openapi/admin/models/AccreditationStatus';
import type {Error} from 'src/openapi/admin/models/Error';


type Props = {
    accountSettingsLoading: boolean,
    accountSettingsLoadingError: Error | null,
    accountSettings?: AccountSettings,
    onChange: (accountSettings: AccountSettings) => void,
    disabled?: boolean,
    isAccountEdit?: boolean,
    onFieldChange: (parameterName: string) => void,
    setPreset: (preset: AccountPreset) => void,
    preset: AccountPreset,
    showPresetValidation?: boolean,
};

type OnCheckBoxChange = (data: {event: React.SyntheticEvent<HTMLInputElement, Event>, name: string}) => void;

const SECTION_NAME = 'Account Settings';
const UNTRACKED_ACCOUNT = 'Choosing this option implies significant changes to your account';

export const AccountSettingsSection = (props: Props) => {

    const {hasAccess} = useAdminAccessRight();
    const {disabled, onFieldChange, isAccountEdit, showPresetValidation} = props;
    const {businessLines} = useBusinessLines();
    const {onChange} = props;
    const {preset, setPreset} = props;

    const data = props.accountSettings ? mapAccountSettingsToData(props.accountSettings) : null;


    const change = useCallback((settingsData: AccountSettingsData) => {
        onChange(mapAccountDataToSettings(settingsData));
    }, [onChange]);

    const onCheckBoxChange = useCallback<OnCheckBoxChange>(changeData => {
        if (data) {
            const name = changeData.name;
            change({
                ...data,
                [name]: changeData.event.currentTarget.checked,
            });
        }
    }, [data, change]);

    const onPresetChange = useCallback((newPreset: AccountPreset) => {
        onFieldChange('Entitlement preset');
        setPreset(newPreset);
    }, [setPreset, onFieldChange]);

    const onProjectCodeEnableChange = useCallback<OnCheckBoxChange>(changeData => {
        onFieldChange('Project Codes: Enabled');
        if (data) {
            const projectCodeEnabled = changeData.event.currentTarget.checked;
            change({
                ...data,
                projectCodeEnabled,
                projectCodeRequired: projectCodeEnabled ? data.projectCodeRequired : false,
            });
        }
    }, [data, change, onFieldChange]);

    const onUntrackedChange = useCallback<OnCheckBoxChange>(changeData => {
        onFieldChange('Untracked Account');
        if (data) {
            const untrackedAccount = changeData.event.currentTarget.checked;
            change({
                ...data,
                untrackedAccount,
                fullStory: untrackedAccount ? false : data.fullStory,
                liveAgent: untrackedAccount ? false : data.liveAgent,
            });
        }
    }, [data, change, onFieldChange]);

    const setAccreditationStatus = useCallback((status: AccreditationStatus) => {
        onFieldChange('Accreditation Status');
        if (data) {
            change({
                ...data,
                accreditationStatus: status,
            });
        }
    }, [data, change, onFieldChange]);

    const onBusinessLineChange = useCallback<OnCheckBoxChange>(changeData => {
        onFieldChange('Business Lines');
        if (data) {
            const checked = changeData.event.currentTarget.checked;
            const name = changeData.name;
            change({
                ...data,
                [MAP_BUSINESS_LINE_FIELD_NAMES[name]]: checked,
            });
        }
    }, [data, change, onFieldChange]);

    const isAccreditationStatusDisabled = useMemo(() => {
        return disabled || !hasAccess(ACCESS_RIGHTS.MANAGE_SYSTEM_PROPERTIES);
    }, [disabled, hasAccess]);

    const accreditationStatusOptions = useMemo(() =>
        ACCREDITATION_STATUS_DICTIONARY.map(({code, description}) => (
            <ComboboxItem key={code} caption={description} value={code}/>
        )), []);

    return (
        <Skeleton shown={props.accountSettingsLoading} view={<AccountSettingsSkeleton/>}>
            <ServerResponseHandler
                smallView={true}
                sectionName={SECTION_NAME}
                serverError={props.accountSettingsLoadingError}
                accessRight={ACCESS_RIGHTS.ACCOUNT_SCREEN_VIEW as AccessRight}>
                <Group direction="v">
                    <FormSection headline={SECTION_NAME}>
                        <Group alignItems="start" direction="v">
                            <Group gap="s">
                                <CheckBox {...{
                                    disabled,
                                    label: 'Untrack account',
                                    checked: data?.untrackedAccount,
                                    name: 'untrackedAccount',
                                    onCheckBoxChange: onUntrackedChange,
                                }}
                                />
                                <Info type={{popover: UNTRACKED_ACCOUNT}}/>
                            </Group>
                            <Group alignItems="start">
                                <WidthItem width={240}>
                                    <Label>Project Codes</Label>
                                    <CheckBox {...{
                                        disabled,
                                        label: 'Enabled',
                                        checked: data?.projectCodeEnabled,
                                        name: 'projectCodeEnabled',
                                        onCheckBoxChange: onProjectCodeEnableChange,
                                    }}
                                    />
                                    <CheckBox {...{
                                        label: 'Required',
                                        disabled: disabled || !data?.projectCodeEnabled,
                                        checked: data?.projectCodeRequired,
                                        name: 'projectCodeRequired',
                                        onCheckBoxChange: e => {
                                            onFieldChange('Project Codes: Required');
                                            onCheckBoxChange(e);
                                        },
                                    }}
                                    />
                                </WidthItem>
                                <WidthItem width={240}>
                                    <Label>Business Lines</Label>
                                    {businessLines.map(businessLine => {
                                        return (
                                            <CheckBox
                                                key={businessLine.code}
                                                {...{
                                                    disabled,
                                                    label: businessLine.description,
                                                    checked: !!data &&
                                                        data[MAP_BUSINESS_LINE_FIELD_NAMES[businessLine.code]],
                                                    name: businessLine.code,
                                                    onCheckBoxChange: onBusinessLineChange,
                                                }}
                                            />
                                        );
                                    })}
                                </WidthItem>
                                <WidthItem width={240}>
                                    <Label>Support</Label>
                                    <CheckBox {...{
                                        label: 'FullStory - screen recording',
                                        checked: data?.fullStory,
                                        disabled: disabled || data?.untrackedAccount,
                                        name: 'fullStory',
                                        onCheckBoxChange: e => {
                                            onFieldChange('FullStory');
                                            onCheckBoxChange(e);
                                        },
                                    }}
                                    />
                                    <CheckBox {...{
                                        label: 'Live Agent',
                                        checked: data?.liveAgent,
                                        disabled: disabled || data?.untrackedAccount,
                                        name: 'liveAgent',
                                        onCheckBoxChange: e => {
                                            onFieldChange('Live Agent');
                                            onCheckBoxChange(e);
                                        },
                                    }}
                                    />
                                    <CheckBox {...{
                                        disabled,
                                        label: 'Salesforce Communities',
                                        checked: data?.salesforce,
                                        name: 'salesforce',
                                        onCheckBoxChange: e => {
                                            onFieldChange('Salesforce communities');
                                            onCheckBoxChange(e);
                                        },
                                    }}
                                    />
                                    <CheckBox {...{
                                        disabled,
                                        label: 'PitchBook Pioneer',
                                        checked: data?.pbPioneer,
                                        name: 'pbPioneer',
                                        onCheckBoxChange: e => {
                                            onFieldChange('PitchBook Pioneer');
                                            onCheckBoxChange(e);
                                        },
                                    }}
                                    />
                                </WidthItem>
                            </Group>
                        </Group>
                    </FormSection>
                    <Group direction="v" gap="l">
                        <FormSection headline="Entitlements settings">
                            <WidthItem width={300}>
                                <Label htmlFor="preset-suggest">
                                    <RequiredMark label="Entitlement preset"/>
                                </Label>
                                <EntitlementPresetsCombobox
                                    preset={preset}
                                    setPreset={onPresetChange}
                                    disabled={disabled}
                                    showValidationError={showPresetValidation}
                                />
                            </WidthItem>
                        </FormSection>
                        {isAccountEdit ? (
                            <FormSection headline="Accreditation Settings">
                                <WidthItem width={300}>
                                    <Label>Accreditation Status</Label>
                                    <ComboBox
                                        disabled={isAccreditationStatusDisabled}
                                        value={data?.accreditationStatus}
                                        onChange={setAccreditationStatus}>
                                        {accreditationStatusOptions}
                                    </ComboBox>
                                </WidthItem>
                            </FormSection>
                        ) : null}
                    </Group>
                </Group>
            </ServerResponseHandler>
        </Skeleton>
    );
};
